import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import type { HistoricalLoadStatus } from "@/lib/order-load-status";

export type LoadStatusAtDeliveryMap = Map<string, HistoricalLoadStatus>;

interface Row {
  order_id: string;
  load_status: string | null;
  occurred_at: string | null;
  event_type: string | null;
}

/**
 * Batch-resolves "what was the trailer's load status when this order was
 * delivered?" for the given order ids. Only delivered orders need it — the rest
 * display the trailer's live state.
 */
export function useOrderLoadStatusAtDelivery(orderIds: string[] | undefined) {
  const ids = useMemo(() => [...new Set(orderIds ?? [])].sort(), [orderIds]);

  const { data } = useQuery({
    queryKey: ["order-load-status-at-delivery", ids],
    enabled: ids.length > 0,
    staleTime: 5 * 60_000,
    queryFn: async () => {
      const { data, error } = await supabase.rpc("order_load_status_at_delivery", {
        p_order_ids: ids,
      });
      if (error) throw error;
      return (data ?? []) as Row[];
    },
  });

  return useMemo<LoadStatusAtDeliveryMap>(() => {
    const map: LoadStatusAtDeliveryMap = new Map();
    (data ?? []).forEach((r) => {
      if (r.order_id) {
        map.set(r.order_id, {
          load_status: r.load_status,
          occurred_at: r.occurred_at,
          event_type: r.event_type,
        });
      }
    });
    return map;
  }, [data]);
}
